import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { recomputeUserAssignments } from "@/hooks/use-budget-lines";

/**
 * Superadmin-mutasjon for å flytte en time_entry til en annen budget_line
 * etter at importen har kjørt. Bruker når en feilmappet Monday-import
 * skal rettes opp uten å re-importere alt.
 *
 * Re-aggregerer `budget_line_assignments` for berørt user, slik at
 * Timetildeling-fanen reflekterer nye summer umiddelbart. Setter
 * foreldreløse assignments til 0t (samme logikk som ved import).
 */
export function useReassignTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      time_entry_id: string;
      project_id: string;
      new_budget_line_id: string | null;
    }) => {
      const { data: entry, error: getErr } = await supabase
        .from("time_entries")
        .select("user_id, monday_item_id")
        .eq("id", input.time_entry_id)
        .single();
      if (getErr) throw new Error(getErr.message);
      if (!entry?.user_id) throw new Error("time_entry mangler user_id");

      // VIB-799: etter splittet ligger ofte flere rader per Monday-item
      // (én per session). Reassign treffer alle rader for samme item, ellers
      // ville bare én session bli flyttet og resten henge igjen på gammel
      // budget_line. Manuelle entries (uten monday_item_id) treffer kun
      // den spesifikke raden.
      const updateQuery = supabase
        .from("time_entries")
        .update({
          budget_line_id: input.new_budget_line_id,
          // Manuelt valg fra UI låses så neste Monday-import ikke overskriver.
          budget_line_id_locked: true,
        });
      const { error: updErr } = entry.monday_item_id != null
        ? await updateQuery.eq("monday_item_id", entry.monday_item_id)
        : await updateQuery.eq("id", input.time_entry_id);
      if (updErr) throw new Error(updErr.message);

      await recomputeUserAssignments(entry.user_id);
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "time_entries", { projectId: vars.project_id });
      toast.success("Tidsregistrering flyttet til ny budgetlinje");
    },
    onError: (err) => {
      toast.error(`Kunne ikke flytte: ${(err as Error).message}`);
    },
  });
}
